import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer'

interface ContactInfo {
  name: string
  email: string
}

interface PlaygroundPdfDocumentProps {
  diagramImage: string // Base64 PNG data URL
  title?: string
  customerName?: string
  customerLogoUrl?: string | null
  generatedAt: string // ISO date string
  seContact?: ContactInfo
  aeContact?: ContactInfo
}

// A4 Landscape: 842 x 595 points
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 30,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    paddingBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  customerLogo: {
    width: 50,
    height: 50,
    objectFit: 'contain',
  },
  customerName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333333',
  },
  headerRight: {
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  title: {
    fontSize: 14,
    color: '#666666',
  },
  date: {
    fontSize: 10,
    color: '#999999',
  },
  diagramContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  diagram: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  footer: {
    marginTop: 15,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#E0E0E0',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerLeft: {
    flexDirection: 'column',
    flex: 1,
  },
  footerCenter: {
    flexDirection: 'column',
    alignItems: 'center',
    flex: 2,
  },
  footerRight: {
    flexDirection: 'column',
    alignItems: 'flex-end',
    flex: 1,
  },
  footerText: {
    fontSize: 8,
    color: '#999999',
  },
  contactSection: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 40,
  },
  contactBlock: {
    flexDirection: 'column',
  },
  contactLabel: {
    fontSize: 7,
    color: '#666666',
    fontWeight: 'bold',
    marginBottom: 2,
  },
  contactName: {
    fontSize: 8,
    color: '#333333',
  },
  contactEmail: {
    fontSize: 7,
    color: '#666666',
  },
})

export function PlaygroundPdfDocument({
  diagramImage,
  title = 'HiveMQ Solution Architecture',
  customerName,
  customerLogoUrl,
  generatedAt,
  seContact,
  aeContact,
}: PlaygroundPdfDocumentProps) {
  const hasContacts = seContact?.name || seContact?.email || aeContact?.name || aeContact?.email

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {customerLogoUrl && <Image src={customerLogoUrl} style={styles.customerLogo} />}
            {customerName && <Text style={styles.customerName}>{customerName}</Text>}
            {!customerName && !customerLogoUrl && (
              <Text style={styles.customerName}>Architecture Diagram</Text>
            )}
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.title}>{title}</Text>
            <Text style={styles.date}>Generated: {new Date(generatedAt).toLocaleDateString()}</Text>
          </View>
        </View>

        {/* Diagram */}
        <View style={styles.diagramContainer}>
          <Image src={diagramImage} style={styles.diagram} />
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <View style={styles.footerLeft}>
            <Text style={styles.footerText}>Created with HiveMQ Stories 2.0</Text>
          </View>
          {hasContacts && (
            <View style={styles.footerCenter}>
              <View style={styles.contactSection}>
                {(seContact?.name || seContact?.email) && (
                  <View style={styles.contactBlock}>
                    <Text style={styles.contactLabel}>Solutions Engineer</Text>
                    {seContact.name && <Text style={styles.contactName}>{seContact.name}</Text>}
                    {seContact.email && <Text style={styles.contactEmail}>{seContact.email}</Text>}
                  </View>
                )}
                {(aeContact?.name || aeContact?.email) && (
                  <View style={styles.contactBlock}>
                    <Text style={styles.contactLabel}>Account Executive</Text>
                    {aeContact.name && <Text style={styles.contactName}>{aeContact.name}</Text>}
                    {aeContact.email && <Text style={styles.contactEmail}>{aeContact.email}</Text>}
                  </View>
                )}
              </View>
            </View>
          )}
          <View style={styles.footerRight}>
            <Text style={styles.footerText}>www.hivemq.com</Text>
          </View>
        </View>
      </Page>
    </Document>
  )
}
